import { useEffect, useState, useRef, useCallback } from 'react'
import { MapStateName } from '@oaisd/michdev.components.react'
import type { Address } from '../models/Address'

export enum MapModule {
  AutoSuggest = 'Microsoft.Maps.AutoSuggest',
  SpatialMath = 'Microsoft.Maps.SpatialMath'
}

/**
 * Converts Bing Maps suggestion result to Address format.
 */
export function mapSuggestionToAddress (result: Microsoft.Maps.ISuggestionResult, baseAddress?: Partial<Address>): Address {
  return {
    id: baseAddress?.id ?? 0,
    street: result.address.addressLine ?? '',
    city: result.address.locality ?? '',
    state: MapStateName(result.address.adminDistrict ?? ''),
    zip: result.address.postalCode ?? '',
    apartment: baseAddress?.apartment ?? ''
  }
}

interface MapProps {
  selectFunction?: (address: Address) => void
  inputRef?: React.RefObject<HTMLInputElement>
  containerRef?: React.RefObject<HTMLDivElement>
  modules: MapModule[]
  hasRendered?: boolean
  baseAddress?: Partial<Address>
}

interface ReturnProps {
  isLoaded: boolean
  hasError: boolean
}

const BING_SCRIPT_ID = 'bing-maps-script'
const BING_API_KEY = process.env.REACT_APP_BING_KEY ?? ''

/**
 * Creates a hidden map instance with credentials.
 * Required for AutoSuggest to authenticate API requests.
 */
const createHiddenMap = (apiKey: string): { map: Microsoft.Maps.Map, div: HTMLDivElement } => {
  const div = document.createElement('div')
  div.style.display = 'none'
  document.body.appendChild(div)

  const map = new Microsoft.Maps.Map(div, {
    credentials: apiKey
  })

  return { map, div }
}

/**
 * Cleans up AutoSuggest manager, map, and DOM elements.
 */
const cleanupAutoSuggest = (
  manager: Microsoft.Maps.AutosuggestManager | null,
  map: Microsoft.Maps.Map | null | undefined,
  div: HTMLDivElement
): void => {
  // Dispose manager
  if (manager !== null) {
    try {
      manager.dispose()
    } catch (error) {
      console.error('Error disposing AutosuggestManager:', error)
    }
  }

  // Dispose map
  if (map !== null && map !== undefined) {
    try {
      map.dispose()
    } catch (error) {
      console.error('Error disposing map:', error)
    }
  }

  // Remove DOM element
  if (div.parentNode !== null) {
    div.parentNode.removeChild(div)
  }
}

export function useBingMaps (props: MapProps): ReturnProps {
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const autosuggestManagerRef = useRef<Microsoft.Maps.AutosuggestManager | null>(null)
  const selectFunctionRef = useRef(props.selectFunction)

  // This is the only way to catch Bing Maps credential errors
  useEffect(() => {
    const originalConsoleError = console.error

    console.error = (...args: any[]) => {
      // Check if this is a Bing Maps credential error
      const message = String(args[0] ?? '')
      if (message.includes('specified credentials are invalid') ||
          message.includes('Invalid credentials')) {
        setHasError(true)
      }
      // Call original console.error
      originalConsoleError.apply(console, args)
    }

    // Cleanup: restore original console.error
    return () => {
      console.error = originalConsoleError
    }
  }, [])

  useEffect(() => {
    if (!isLoaded) return
    if (!props.modules.includes(MapModule.AutoSuggest)) return
    if (props.inputRef === undefined || props.containerRef === undefined) return
    if (props.inputRef.current === null || props.containerRef.current === null) return
    if (autosuggestManagerRef.current !== null) return // Already attached
    if (typeof Microsoft === 'undefined' || Microsoft.Maps === undefined) return

    try {
      const inputElement = props.inputRef.current
      const containerElement = props.containerRef.current

      // Create hidden map with credentials (required for AutoSuggest authentication)
      const { map, div: hiddenMapDiv } = createHiddenMap(BING_API_KEY)

      // Create and attach AutoSuggest manager
      const manager = new Microsoft.Maps.AutosuggestManager({
        placeSuggestions: false,
        map
      })

      manager.attachAutosuggest(
        `#${inputElement.id}`,
        `#${containerElement.id}`,
        (result: Microsoft.Maps.ISuggestionResult) => {
          const address = mapSuggestionToAddress(result, props.baseAddress)
          selectFunctionRef.current?.(address)
        }
      )

      autosuggestManagerRef.current = manager

      // Cleanup on unmount
      return () => {
        cleanupAutoSuggest(autosuggestManagerRef.current, map, hiddenMapDiv)
        autosuggestManagerRef.current = null
      }
    } catch (error) {
      console.error('Error attaching AutoSuggest:', error)
      setHasError(true)
    }
  }, [isLoaded, props.modules, props.inputRef, props.containerRef])

  const initializeModules = useCallback(() => {
    if (typeof Microsoft === 'undefined' || Microsoft.Maps === undefined) {
      return
    }

    Microsoft.Maps.loadModule(props.modules, {
      callback: () => {
        setIsLoaded(true)
      },
      errorCallback: () => {
        console.error('Error loading Bing Maps modules')
        setHasError(true)
      },
      credentials: BING_API_KEY
    })
  }, [props.modules])

  useEffect(() => {
    if (props.hasRendered === false) return

    // Check if script already loaded
    const existingScript = document.getElementById(BING_SCRIPT_ID)
    if (existingScript !== null) {
      if (typeof Microsoft !== 'undefined' && Microsoft.Maps !== undefined) {
        initializeModules()
      }
      return
    }

    (window as any).initMap = () => {
      initializeModules()
    }

    const script = document.createElement('script')
    script.id = BING_SCRIPT_ID
    script.src = 'https://www.bing.com/api/maps/mapcontrol?callback=initMap'
    script.async = true
    script.defer = true
    script.onerror = () => {
      console.error('Failed to load Bing Maps script')
      setHasError(true)
    }

    document.body.appendChild(script)
  }, [props.hasRendered, initializeModules])

  return {
    isLoaded,
    hasError
  }
}
